import moment from 'moment'
import type {
  UserData,
  ActivityType,
  ListKey,
  AnyEntry,
  DisplayEntry,
  Intake,
} from '../types'
import { DATETIME_FORMAT, sortKeysDesc } from './calc'

/** Per-type metadata: which list it lives in and which field holds its name. */
export const TYPE_META: Record<
  ActivityType,
  { listKey: ListKey; nameField: 'activity' | 'operation' | 'exercise'; label: string }
> = {
  cardio: { listKey: 'activity', nameField: 'activity', label: 'Cardio' },
  weights: { listKey: 'operation', nameField: 'operation', label: 'Weights' },
  exercise: { listKey: 'exercise', nameField: 'exercise', label: 'Exercise' },
}

const LISTKEY_TO_TYPE: Record<ListKey, ActivityType> = {
  activity: 'cardio',
  operation: 'weights',
  exercise: 'exercise',
}

export function listKeyForType(type: ActivityType): ListKey {
  return TYPE_META[type].listKey
}

/** Read a named string field from any entry shape. */
function readField(entry: AnyEntry, field: string): string {
  return (entry as unknown as Record<string, string>)[field] || ''
}

export function displayName(entry: AnyEntry, type: ActivityType): string {
  return readField(entry, TYPE_META[type].nameField)
}

/**
 * Group all entries by date (YYYY-MM-DD), newest date first, each date's
 * entries newest-first. Each entry is decorated with type/id/displayName.
 */
export function restructure(data: UserData): Record<string, DisplayEntry[]> {
  const byDate: Record<string, DisplayEntry[]> = {}

  ;(Object.keys(data.lists) as ListKey[]).forEach((listKey) => {
    const type = LISTKEY_TO_TYPE[listKey]
    const list = data.lists[listKey] as AnyEntry[]
    if (!list || !list.length) return

    for (let i = list.length - 1; i >= 0; i--) {
      const entry = list[i]
      if (!entry.datetime) continue
      const dateKey = moment(entry.datetime, DATETIME_FORMAT).format('YYYY-MM-DD')
      if (!byDate[dateKey]) byDate[dateKey] = []
      byDate[dateKey].push({
        ...entry,
        type,
        id: i,
        displayName: displayName(entry, type),
      })
    }
  })

  // Within each date, newest entry first.
  for (const dateKey of Object.keys(byDate)) {
    byDate[dateKey].sort((a, b) => (a.datetime < b.datetime ? 1 : a.datetime > b.datetime ? -1 : 0))
  }

  return sortKeysDesc(byDate)
}

/** A single item in the home timeline — either a Move or an Intake. */
export type TimelineItem =
  | { kind: 'move'; datetime: string; entry: DisplayEntry }
  | { kind: 'intake'; datetime: string; intake: Intake; id: number }

/**
 * Combined home timeline: Moves and Intake grouped by date (newest first),
 * each tagged so the UI can render the right card.
 */
export function buildTimeline(data: UserData): Record<string, TimelineItem[]> {
  const moves = restructure(data)
  const byDate: Record<string, TimelineItem[]> = {}

  for (const date of Object.keys(moves)) {
    byDate[date] = moves[date].map((entry) => ({ kind: 'move' as const, datetime: entry.datetime, entry }))
  }

  ;(data.intake ?? []).forEach((intake, id) => {
    if (!intake.datetime) return
    const dateKey = moment(intake.datetime, DATETIME_FORMAT).format('YYYY-MM-DD')
    if (!byDate[dateKey]) byDate[dateKey] = []
    byDate[dateKey].push({ kind: 'intake', datetime: intake.datetime, intake, id })
  })

  for (const date of Object.keys(byDate)) {
    byDate[date].sort((a, b) => (a.datetime < b.datetime ? 1 : a.datetime > b.datetime ? -1 : 0))
  }
  return sortKeysDesc(byDate)
}

/** Most-recent entry across all three lists, or null. */
export function getLatestEntry(data: UserData): AnyEntry | null {
  let latest: AnyEntry | null = null
  ;(['activity', 'operation', 'exercise'] as ListKey[]).forEach((listKey) => {
    const list = data.lists[listKey] as AnyEntry[]
    if (list && list.length) {
      const candidate = list[list.length - 1]
      if (!latest || moment(candidate.datetime).isAfter(moment(latest.datetime))) {
        latest = candidate
      }
    }
  })
  return latest
}

/** Friendly "days since last move" line for the header. */
export function getLastSessionText(data: UserData): string {
  const latest = getLatestEntry(data)
  if (!latest) return '—'

  const latestDate = moment(latest.datetime)
  const daysDiff = Math.floor(moment.duration(moment().diff(latestDate)).asDays())

  if (latestDate.isSame(moment(), 'day')) return 'You moved-it TODAY!'
  if (daysDiff < 2) return 'You moved-it yesterday!'
  return `${daysDiff} day${daysDiff === 1 ? '' : 's'} since last move.`
}

/**
 * Entries for a given activity name, newest first, up to `limit`. Optionally
 * only those before `beforeDate` (used by the workout report's day-over-day
 * comparison). Replaces the old lodash chain.
 */
export function getEntriesActivityDesc(
  data: UserData,
  type: ActivityType,
  activity: string,
  limit = 2,
  beforeDate: string | null = null,
): AnyEntry[] {
  const listKey = listKeyForType(type)
  const list = (data.lists[listKey] as AnyEntry[]) || []
  const needle = activity.toLowerCase()

  let filtered = list.filter((entry) =>
    Object.values(entry).some(
      (val) => typeof val === 'string' && val.toLowerCase().includes(needle),
    ),
  )

  if (beforeDate) {
    const cutoff = new Date(beforeDate).setHours(0, 0, 0, 0)
    filtered = filtered.filter((entry) => new Date(entry.datetime).setHours(0, 0, 0, 0) < cutoff)
  }

  return filtered
    .sort((a, b) => (a.datetime < b.datetime ? 1 : a.datetime > b.datetime ? -1 : 0))
    .slice(0, limit)
}

/**
 * Unique entries by name for a type, keeping the most recent of each name.
 * Used both to populate autocomplete and to prefill the form from history.
 */
export function uniqueEntriesByName(data: UserData, type: ActivityType): AnyEntry[] {
  const { listKey, nameField } = TYPE_META[type]
  const list = (data.lists[listKey] as AnyEntry[]) || []
  const seen = new Map<string, AnyEntry>()

  // Iterate newest→oldest so the first seen per name is the most recent.
  for (let i = list.length - 1; i >= 0; i--) {
    const entry = list[i]
    const name = readField(entry, nameField)
    if (name && !seen.has(name)) seen.set(name, entry)
  }

  return Array.from(seen.values()).sort((a, b) =>
    readField(a, nameField).localeCompare(readField(b, nameField)),
  )
}

/**
 * Past Food & Drink logs deduped by name, most recent of each first. Powers the
 * Food form's type-ahead so a repeat meal can be re-logged without a fresh API
 * lookup.
 */
export function uniqueIntakesByName(data: UserData): Intake[] {
  const sorted = [...(data.intake ?? [])].sort((a, b) =>
    a.datetime < b.datetime ? 1 : a.datetime > b.datetime ? -1 : 0,
  )
  const seen = new Map<string, Intake>()
  for (const it of sorted) {
    const key = (it.name || it.query || '').trim().toLowerCase()
    if (key && !seen.has(key)) seen.set(key, it)
  }
  return Array.from(seen.values())
}
